import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getLocations, getBuildings, createBuilding, updateBuilding, deleteBuilding } from "../lib/api";

export const BuildingsManager = ({ open, onClose, onChanged }) => {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [floors, setFloors] = useState("");
  const [rooms, setRooms] = useState("");
  const [editId, setEditId] = useState(null);

  const load = async () => setList(await getBuildings());

  useEffect(() => {
    if (!open) return;
    (async () => {
      const locs = await getLocations();
      setLocations(locs);
      setLocationId((prev) => prev || locs[0]?.id || "");
      await load();
      setName(""); setFloors(""); setRooms(""); setEditId(null);
    })();
  }, [open]);

  if (!open) return null;

  const filtered = list.filter((b) => b.location_id === locationId);

  const save = async (e) => {
    e.preventDefault();
    if (!locationId) return toast.error("Pilih lokasi dulu");
    if (!name.trim()) return toast.error("Nama gedung wajib diisi");
    const floorList = floors.split("\n").map((r) => r.trim()).filter(Boolean);
    const roomList = rooms.split("\n").map((r) => r.trim()).filter(Boolean);
    const payload = { location_id: locationId, name: name.trim(), floors: floorList, rooms: roomList };
    try {
      if (editId) await updateBuilding(editId, payload);
      else await createBuilding(payload);
      toast.success("Gedung tersimpan");
      setName(""); setFloors(""); setRooms(""); setEditId(null);
      await load(); onChanged && onChanged();
    } catch { toast.error("Gagal menyimpan gedung"); }
  };

  const onDelete = async (b) => {
    if (!window.confirm(`Hapus gedung "${b.name}"?`)) return;
    await deleteBuilding(b.id);
    toast.success("Gedung dihapus");
    await load(); onChanged && onChanged();
  };

  const startEdit = (b) => { setEditId(b.id); setName(b.name); setFloors((b.floors || []).join("\n")); setRooms((b.rooms || []).join("\n")); };

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 outline-none";

  return (
    <div className="no-print fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-auto flex flex-col max-h-[92vh] animate-fade-up" data-testid="buildings-modal">
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 to-teal-800 text-white flex justify-between items-center">
          <h3 className="text-base sm:text-lg font-bold flex items-center gap-2"><i className="ph-bold ph-buildings text-teal-300" /> Kelola Gedung & Ruang</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10" data-testid="buildings-close"><i className="ph-bold ph-x text-xl" /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-5">
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Lokasi</label>
            <select value={locationId} onChange={(e) => { setLocationId(e.target.value); setEditId(null); setName(""); setRooms(""); }} className={`${inputCls} bg-white font-semibold`} data-testid="buildings-location-select">
              {locations.length === 0 && <option value="">(Belum ada lokasi)</option>}
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <form onSubmit={save} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Nama Gedung *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="mis. Gedung Joeang" data-testid="building-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Daftar Lantai <span className="font-normal text-slate-400">(satu per baris)</span></label>
                <textarea value={floors} onChange={(e) => setFloors(e.target.value)} rows={4} className={inputCls} placeholder={"Lantai 1\nLantai 2\nLantai 3"} data-testid="building-floors" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Daftar Ruang <span className="font-normal text-slate-400">(satu per baris)</span></label>
                <textarea value={rooms} onChange={(e) => setRooms(e.target.value)} rows={4} className={inputCls} placeholder={"R. Rapat\nLab. Komputer\nKelas 1"} data-testid="building-rooms" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              {editId && <button type="button" onClick={() => { setEditId(null); setName(""); setRooms(""); }} className="px-3 py-2 border border-slate-300 text-slate-700 text-xs font-bold rounded-lg">Batal Edit</button>}
              <button type="submit" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-lg" data-testid="building-save-btn">
                {editId ? "Perbarui Gedung" : "Tambah Gedung"}
              </button>
            </div>
          </form>

          <div className="space-y-2">
            {filtered.length === 0 && <p className="text-center text-xs text-slate-400 py-4">Belum ada gedung untuk lokasi ini.</p>}
            {filtered.map((b) => (
              <div key={b.id} className="flex items-start justify-between gap-3 p-3 border border-slate-200 rounded-xl" data-testid={`building-row-${b.id}`}>
                <div className="min-w-0">
                  <p className="font-bold text-slate-800 text-sm">{b.name}</p>
                  <p className="text-xs text-slate-500">{(b.floors || []).length} lantai • {(b.rooms || []).length} ruang{(b.rooms || []).length ? `: ${b.rooms.join(", ")}` : ""}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => startEdit(b)} className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold" data-testid={`building-edit-${b.id}`}><i className="ph-bold ph-pencil-simple" /></button>
                  <button onClick={() => onDelete(b)} className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg text-xs font-bold" data-testid={`building-delete-${b.id}`}><i className="ph-bold ph-trash" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
